import { InspectorIcon } from "../../InspectorIcon";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, FileDiffIcon } from "lucide-react";
import { Button } from "../../ui/button";
import ChatMarkdown from "../../ChatMarkdown";
import { InspectionStatusBadge } from "./InspectionStatus";
import { inspectionStatusCopy, type InspectionRun } from "./inspectorView";
import { displayChangeComment } from "../finding/changes/changeDiscussion";

type Props = { run: InspectionRun; orgSegment: string; findingId: string };
export function FindingReviewProgress({
  run,
  orgSegment,
  showRequest = false,
  onContinue,
  continuing = false,
}: Props & { showRequest?: boolean; onContinue?: () => void; continuing?: boolean }) {
  return (
    <div
      aria-label="Inspector review"
      className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2 text-xs"
    >
      <span className="inline-flex items-center gap-1.5 font-medium"><InspectorIcon className="size-3.5" />Inspector</span>
      <InspectionStatusBadge status={run.status} />
      <Link
        to="/$org/compliance/inspector/$threadId"
        params={{ org: orgSegment, threadId: run.threadId }}
        className="ml-auto inline-flex items-center gap-1 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
      >
        View run
        <ArrowUpRightIcon aria-hidden className="size-3" />
      </Link>
      {onContinue && (run.status === "interrupted" || run.status === "failed") ? (
        <Button variant="outline" size="sm" disabled={continuing} onClick={onContinue}>
          {continuing ? "Continuing…" : "Continue review"}
        </Button>
      ) : null}
      {showRequest && run.review?.instruction ? (
        <blockquote className="w-full border-l-2 border-border pl-3 text-sm leading-6">
          <span className="mb-1 block text-xs text-muted-foreground">Review request</span>
          <p className="whitespace-pre-wrap break-words">
            {displayChangeComment(run.review.instruction)}
          </p>
        </blockquote>
      ) : null}
      <p className="w-full whitespace-pre-wrap break-words leading-5 text-muted-foreground">
        {run.status === "completed"
          ? "Finished reviewing this request."
          : run.status === "interrupted" || run.status === "failed"
            ? `${inspectionStatusCopy(run)} Previous requests and responses are saved.${run.summary ? ` Last update: ${run.summary}` : ""}`
            : run.summary || inspectionStatusCopy(run)}
      </p>
    </div>
  );
}
export function FindingReviewReply({
  run,
  orgSegment,
  findingId,
  email,
  requestVisible = true,
  commentAnchorPrefix = "",
}: Props & { email: string | null; requestVisible?: boolean; commentAnchorPrefix?: string }) {
  if (run.status !== "completed" || !run.reply) return null;
  return (
    <article aria-label="Inspector reply" className="min-w-0">
      <header className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium"><InspectorIcon className="size-3.5" />Inspector</span>
        <span className="rounded border border-primary/15 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary">
          AI
        </span>
        {requestVisible && run.review?.requestId ? (
          <a
            href={`#review-comment-${commentAnchorPrefix}${run.review.requestId}`}
            className="min-w-0 break-all text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            replied to {email ?? "your request"}
          </a>
        ) : null}
        <time dateTime={run.reply.createdAt} className="text-muted-foreground">
          {new Date(run.reply.createdAt).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </time>
      </header>
      <div className="overflow-hidden rounded-xl border border-primary/15 bg-card">
        <div className="min-w-0 p-4">
          <ChatMarkdown text={run.reply.text} cwd={undefined} className="text-sm" />
        </div>
        <footer className="border-t border-border/60 px-4 py-3">
          <Link
            to="/$org/compliance/findings/$findingId"
            params={{ org: orgSegment, findingId }}
            search={{ tab: "changes" }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-4 hover:underline"
          >
            <FileDiffIcon aria-hidden className="size-3.5" />
            Open changes
          </Link>
        </footer>
      </div>
    </article>
  );
}
